import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces';
import { Employee } from '@app/models/employee/employee.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { ManagerAssignment } from '@app/models/recruiting/manager-assignment.model';
import { HiringTeamMember } from '@app/modules/applicant-tracker/interfaces/hiring-team-member.interface';
import { HiringTeamAssignmentService } from '@app/modules/applicant-tracker/services';
import { AnalyticService, NotifyService } from '@app/services';

@Component({
    templateUrl: './manage-hiring-team.template.html',
})
export class ManageHiringTeamView implements OnInit, CanDeactivateComponent {
    jobPosting: JobPosting;
    initialHiringTeam: HiringTeamMember[] = [];
    currentHiringTeam: HiringTeamMember[] = [];
    employees: Employee[] = [];
    isWarningDialogOpen = false;
    canNavigateAway = false;
    onEdit = false;
    isLoading = false;

    constructor(
        public route: ActivatedRoute,
        private hiringTeamAssignmentService: HiringTeamAssignmentService,
        private notify: NotifyService,
        private router: Router,
        private analyticService: AnalyticService
    ) {
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');
    }

    ngOnInit(): void {
        this.initialHiringTeam = this.jobPosting.managerAssignments.map((managerAssignment: ManagerAssignment) => {
            managerAssignment.employee.accessLevelId = managerAssignment.accessLevelId;
            return { employee: managerAssignment.employee, onSelection: false };
        });

        this.currentHiringTeam = [...this.initialHiringTeam];

        this.employees = this.jobPosting.managerAssignments.map((managerAssignment: ManagerAssignment) => {
            managerAssignment.employee.accessLevelId = managerAssignment.accessLevelId;
            return managerAssignment.employee;
        });
    }

    canDeactivate(): boolean {
        // If no edit is made then they can navigate away immediately.
        if (!this.onEdit) {
            return true;
        }

        if (!this.canNavigateAway) {
            this.isWarningDialogOpen = true;
        }

        return this.canNavigateAway;
    }

    goBack(): void {
        this.canNavigateAway = true;

        this.router.navigate(['../../information'], { relativeTo: this.route });
    }

    closeDialog(): void {
        this.isWarningDialogOpen = false;
    }

    onHiringTeamUpdate(hiringTeamMembers: HiringTeamMember[]): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingInfoHiringTeam);
        this.currentHiringTeam = hiringTeamMembers;
        this.employees = this.currentHiringTeam.map((hiringTeamMember: HiringTeamMember) => {
            return hiringTeamMember.employee;
        });
    }

    async onSave(): Promise<void> {
        this.canNavigateAway = true;

        const attachFiltered = this.currentHiringTeam
            .filter((hiringTeamMember: HiringTeamMember) => {
                // If the user has changed the users level access then do not filter them out.
                if (hiringTeamMember.onSelection) {
                    return true;
                }

                return !this.initialHiringTeam.some(
                    (initialHiringTeamMember: HiringTeamMember) =>
                        initialHiringTeamMember.employee.id === hiringTeamMember.employee.id
                );
            })
            .map((hiringTeamMember: HiringTeamMember) => hiringTeamMember.employee);

        const detachFiltered = this.initialHiringTeam
            .filter((hiringTeamMember: HiringTeamMember) => {
                return !this.currentHiringTeam.some(
                    (initialHiringTeamMember: HiringTeamMember) =>
                        initialHiringTeamMember.employee.id === hiringTeamMember.employee.id
                );
            })
            .map((hiringTeamMember: HiringTeamMember) => hiringTeamMember.employee);

        const attach = this.hiringTeamAssignmentService.createAttach(attachFiltered);
        const detach = this.hiringTeamAssignmentService.createDetach(detachFiltered);

        try {
            this.isLoading = true;
            await this.hiringTeamAssignmentService.assign(this.jobPosting.id, attach, detach);
            this.router.navigate(['../../information'], { relativeTo: this.route });
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }
}
